import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import { VitalsParameter, VitalsRange } from "@/types/vitals";
import styles from "./EditRangeModal.module.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface EditRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  parameter: VitalsParameter.BP | VitalsParameter.GLUCOSE;
  memberId: string;
  currentRange: VitalsRange;
  onSuccess: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function n(v: string) {
  return Number(v);
}

function normRange(a: string, b: string): [number, number] {
  const lo = Math.min(n(a), n(b));
  const hi = Math.max(n(a), n(b));
  return [lo, hi];
}

// ── Range row ─────────────────────────────────────────────────────────────────

const RangeRow: React.FC<{
  label: string;
  minVal: string;
  maxVal: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
  unit?: string;
}> = ({ label, minVal, maxVal, onMinChange, onMaxChange, unit }) => (
  <div className={styles.row}>
    <span className={styles.rowLabel}>{label}</span>
    <div className={styles.rowInputs}>
      <input
        className={styles.input}
        type="number"
        inputMode="numeric"
        value={minVal}
        onChange={(e) => onMinChange(e.target.value)}
      />
      <span className={styles.dash}>-</span>
      <input
        className={styles.input}
        type="number"
        inputMode="numeric"
        value={maxVal}
        onChange={(e) => onMaxChange(e.target.value)}
      />
    </div>
    {unit && <span className={styles.unit}>{unit}</span>}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const EditRangeModal: React.FC<EditRangeModalProps> = ({
  isOpen,
  onClose,
  parameter,
  memberId,
  currentRange,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const isBP = parameter === VitalsParameter.BP;

  // BP state — fall back to system defaults if no custom range saved yet
  const [sysMin, setSysMin] = useState(() =>
    String(currentRange.systolic?.[0] ?? 90)
  );
  const [sysMax, setSysMax] = useState(() =>
    String(currentRange.systolic?.[1] ?? 140)
  );
  const [diaMin, setDiaMin] = useState(() =>
    String(currentRange.diastolic?.[0] ?? 60)
  );
  const [diaMax, setDiaMax] = useState(() =>
    String(currentRange.diastolic?.[1] ?? 90)
  );

  // Glucose state — fall back to system defaults if no custom range saved yet
  const [fastMin, setFastMin] = useState(() =>
    String(currentRange.fasting?.[0] ?? 70)
  );
  const [fastMax, setFastMax] = useState(() =>
    String(currentRange.fasting?.[1] ?? 99)
  );
  const [ppMin, setPpMin] = useState(() =>
    String(currentRange.postPrandial?.[0] ?? 70)
  );
  const [ppMax, setPpMax] = useState(() =>
    String(currentRange.postPrandial?.[1] ?? 139)
  );
  const [randMin, setRandMin] = useState(() =>
    String(currentRange.random?.[0] ?? 70)
  );
  const [randMax, setRandMax] = useState(() =>
    String(currentRange.random?.[1] ?? 139)
  );
  const [bedMin, setBedMin] = useState(() =>
    String(currentRange.beforeBed?.[0] ?? 80)
  );
  const [bedMax, setBedMax] = useState(() =>
    String(currentRange.beforeBed?.[1] ?? 140)
  );

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isBP) {
        await vitalsTrackingService.updateCustomRange(memberId, {
          [VitalsParameter.BP]: {
            systolic: normRange(sysMin, sysMax),
            diastolic: normRange(diaMin, diaMax),
          },
        });
      } else {
        await vitalsTrackingService.updateCustomRange(memberId, {
          [VitalsParameter.GLUCOSE]: {
            fasting: normRange(fastMin, fastMax),
            postPrandial: normRange(ppMin, ppMax),
            random: normRange(randMin, randMax),
            beforeBed: normRange(bedMin, bedMax),
          },
        });
      }
      onSuccess();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const title = isBP
    ? t("vitalsTracker.editRange.titleBP")
    : t("vitalsTracker.editRange.titleGlucose");

  return (
    <FooterDrawer
      open={isOpen}
      onClose={onClose}
      title={title}
      doneText={saving ? t("common.saving") : t("vitalsTracker.editRange.save")}
      donePosition="top"
      allowKeyboard
      onDone={handleSave}
      doneDisabled={saving}
      height={isBP ? "85vh" : "90vh"}
    >
      <div className={styles.fields}>
        <p className={styles.subtitle}>
          {t("vitalsTracker.editRange.subtitle")}
        </p>

        {isBP ? (
          <>
            <RangeRow
              label={t("vitalsTracker.editRange.systolic")}
              minVal={sysMin}
              maxVal={sysMax}
              onMinChange={setSysMin}
              onMaxChange={setSysMax}
            />
            <RangeRow
              label={t("vitalsTracker.editRange.diastolic")}
              minVal={diaMin}
              maxVal={diaMax}
              onMinChange={setDiaMin}
              onMaxChange={setDiaMax}
            />
          </>
        ) : (
          <>
            <RangeRow
              label={t("vitalsTracker.editRange.fasting")}
              minVal={fastMin}
              maxVal={fastMax}
              onMinChange={setFastMin}
              onMaxChange={setFastMax}
              unit="mg/dL"
            />
            <div className={styles.divider} />
            <RangeRow
              label={t("vitalsTracker.editRange.postPrandial")}
              minVal={ppMin}
              maxVal={ppMax}
              onMinChange={setPpMin}
              onMaxChange={setPpMax}
              unit="mg/dL"
            />
            <div className={styles.divider} />
            <RangeRow
              label={t("vitalsTracker.editRange.random")}
              minVal={randMin}
              maxVal={randMax}
              onMinChange={setRandMin}
              onMaxChange={setRandMax}
              unit="mg/dL"
            />
            <div className={styles.divider} />
            <RangeRow
              label={t("vitalsTracker.editRange.beforeBed")}
              minVal={bedMin}
              maxVal={bedMax}
              onMinChange={setBedMin}
              onMaxChange={setBedMax}
              unit="mg/dL"
            />
          </>
        )}
      </div>
    </FooterDrawer>
  );
};

export default EditRangeModal;
